import { supabase } from '../lib/supabase';

const USE_PRESIGNED_UPLOAD =
  import.meta.env.VITE_USE_PRESIGNED_UPLOAD !== 'false';

function getFunctionsBaseUrl() {
  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) {
    throw new Error('VITE_SUPABASE_URL manquant dans la configuration.');
  }
  return `${base}/functions/v1`;
}

async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Session expirée — reconnectez-vous.');
  }
  return session.access_token;
}

function isPresignedUnavailableError(err) {
  const msg = String(err?.message || err || '').toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('not_found') ||
    msg.includes('not found') ||
    msg.includes('requested function was not found') ||
    msg.includes('réseau bloqué vers le stockage') ||
    msg.includes('cors bunny') ||
    msg.includes('use_server_upload')
  );
}

function formatInvokeError(functionName, err, response, data) {
  if (response?.status === 404 || data?.code === 'NOT_FOUND') {
    return `Fonction ${functionName} non déployée sur Supabase.`;
  }
  if (isPresignedUnavailableError(err)) {
    return `Service ${functionName} indisponible (réseau ou fonction absente).`;
  }
  return data?.error || data?.message || err?.message || `Échec de l’appel ${functionName}`;
}

async function invokeEdgeFunction(functionName, body) {
  const token = await getAccessToken();
  let response;
  try {
    response = await fetch(`${getFunctionsBaseUrl()}/${functionName}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    const wrapped = new Error(formatInvokeError(functionName, networkErr, null, null));
    wrapped.cause = networkErr;
    throw wrapped;
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(formatInvokeError(functionName, null, response, data));
    if (
      response.status === 404 ||
      response.status === 501 ||
      data?.code === 'NOT_FOUND' ||
      data?.code === 'USE_SERVER_UPLOAD'
    ) {
      err.isPresignedUnavailable = true;
    }
    throw err;
  }
  return data;
}

export function putFileToPresignedUrl(file, uploadUrl, headers = {}, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status });
      } else {
        reject(
          new Error(
            `Échec envoi vers le stockage (${xhr.status}). Vérifiez le CORS Bunny (méthode PUT).`
          )
        );
      }
    };

    xhr.onerror = () =>
      reject(
        new Error(
          'Réseau bloqué vers le stockage (CORS Bunny ou URL invalide).'
        )
      );
    xhr.send(file);
  });
}

async function uploadViaServerProxy({
  file,
  fileKind,
  caseId,
  patientId,
  scanType,
  photoType,
  category,
  documentType,
  onProgress,
}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('caseId', caseId);
  formData.append('patientId', patientId);
  formData.append('fileKind', fileKind);
  if (scanType) formData.append('scanType', scanType);
  if (photoType) formData.append('photoType', photoType);
  if (category) formData.append('category', category);
  if (documentType) formData.append('documentType', documentType);

  const token = await getAccessToken();

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${getFunctionsBaseUrl()}/upload-to-s3`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('apikey', import.meta.env.VITE_SUPABASE_ANON_KEY);

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      let result = {};
      try {
        result = JSON.parse(xhr.responseText || '{}');
      } catch {
        /* ignore */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({
          storageKey: result.storageKey,
          canonicalFileName: result.canonicalFileName,
          dbRecord: result.dbRecord,
          processingQueued: result.processingQueued === true,
        });
      } else {
        reject(new Error(result.error || `Échec téléversement serveur (${xhr.status})`));
      }
    };

    xhr.onerror = () =>
      reject(new Error('Impossible de joindre upload-to-s3. Déployez la fonction et les secrets Bunny.'));
    xhr.send(formData);
  });
}

async function runServerProxyFallback(ctx) {
  console.warn('[upload] Repli via upload-to-s3');
  const proxied = await uploadViaServerProxy(ctx);
  return { ...proxied, uploadedBy: ctx.currentUserId };
}

async function uploadFileViaPresigned(ctx) {
  const {
    file,
    fileKind,
    caseId,
    patientId,
    currentUserId,
    scanType,
    photoType,
    category,
    documentType,
    onProgress,
  } = ctx;

  try {
    const presigned = await invokeEdgeFunction('create-presigned-upload', {
      caseId,
      patientId,
      fileKind,
      fileSize: file.size,
      originalFileName: file.name,
      contentType: file.type || undefined,
      scanType,
      photoType,
      documentType,
    });

    try {
      await putFileToPresignedUrl(
        file,
        presigned.uploadUrl,
        presigned.headers || {
          'Content-Type': file.type || presigned.headers?.['Content-Type'],
        },
        onProgress
      );
    } catch (putErr) {
      return runServerProxyFallback(ctx);
    }

    try {
      const confirmed = await invokeEdgeFunction('confirm-file-upload', {
        caseId,
        patientId,
        fileKind,
        storageKey: presigned.storageKey,
        canonicalFileName: presigned.canonicalFileName,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
        originalFileName: file.name,
        scanType,
        photoType,
        category,
        documentType,
      });

      return {
        storageKey: presigned.storageKey,
        canonicalFileName: presigned.canonicalFileName,
        dbRecord: confirmed.dbRecord,
        processingQueued: confirmed.processingQueued === true,
        uploadedBy: currentUserId,
      };
    } catch (confirmErr) {
      if (!isPresignedUnavailableError(confirmErr)) throw confirmErr;
      return runServerProxyFallback(ctx);
    }
  } catch (presignedErr) {
    if (!isPresignedUnavailableError(presignedErr)) {
      throw presignedErr;
    }
    return runServerProxyFallback(ctx);
  }
}

export async function uploadScan(file, patientId, caseId, scanType, userId, options = {}) {
  const normalizedType = scanType.toLowerCase();
  if (!['upper', 'lower'].includes(normalizedType)) {
    throw new Error('scanType must be "upper" or "lower"');
  }

  const ctx = {
    file,
    fileKind: 'scan',
    caseId,
    patientId,
    currentUserId: userId,
    scanType: normalizedType,
    onProgress: options.onProgress,
  };

  if (USE_PRESIGNED_UPLOAD) {
    return uploadFileViaPresigned(ctx);
  }

  const proxied = await uploadViaServerProxy(ctx);
  return { dbRecord: proxied.dbRecord, processingQueued: proxied.processingQueued === true };
}

export async function uploadPhoto(
  file,
  patientId,
  caseId,
  photoType,
  category,
  userId,
  options = {}
) {
  const ctx = {
    file,
    fileKind: 'photo',
    caseId,
    patientId,
    currentUserId: userId,
    photoType,
    category,
    onProgress: options.onProgress,
  };

  if (USE_PRESIGNED_UPLOAD) {
    return uploadFileViaPresigned(ctx);
  }

  const proxied = await uploadViaServerProxy(ctx);
  return { dbRecord: proxied.dbRecord };
}

export async function uploadDocument(
  file,
  patientId,
  caseId,
  documentType,
  userId,
  options = {}
) {
  const ctx = {
    file,
    fileKind: 'document',
    caseId,
    patientId,
    currentUserId: userId,
    documentType,
    onProgress: options.onProgress,
  };

  if (USE_PRESIGNED_UPLOAD) {
    return uploadFileViaPresigned(ctx);
  }

  const proxied = await uploadViaServerProxy(ctx);
  return { dbRecord: proxied.dbRecord };
}

export default {
  uploadScan,
  uploadPhoto,
  uploadDocument,
  putFileToPresignedUrl,
  USE_PRESIGNED_UPLOAD,
};
